import { Loader, Texture, Spritesheet } from 'pixi.js';
import pixiSound from 'pixi-sound';
import { globals } from './globals';

export type LoadedAsset = pixiSound.Sound | Spritesheet | Texture;

export class LoadContext
{
    private static _current: LoadContext = null;
    public static get current() { return LoadContext._current; }

    private loader: Loader;
    private loaded: Map<string, LoadedAsset>;

    /**
     * Makes a new load context.
     * @param isGlobal If true, sets itself up as the global "current" LoadContext
     */
    constructor(isGlobal = false)
    {
        if (isGlobal)
        {
            if (LoadContext._current)
            {
                throw new Error('LoadContext._current is not empty, can\'t become current');
            }
            LoadContext._current = this;
        }
        this.loader = new Loader();
        this.loaded = new Map();
    }

    public add<T extends LoadedAsset = LoadedAsset>(id: string, path: string, cb?: (result?:T) => void)
    {
        if (this.loader.resources[id])
        {
            // don't load again!
            return;
        }
        // Insert texture size variant based on screen size
        path = path.replace('{size}', globals.SMALL_SCREEN || globals.LOW_PERFORMANCE.value ? '@0.5x' : '');
        this.loader.add(id, path, () => {
            this.loaded.set(id, this.getAssetForId(id));
            if (cb)
            {
                cb(this.getAssetForId(id) as T);
            }
        });
    }

    public load()
    {
        return new Promise(resolve => this.loader.load(resolve));
    }

    private getAssetForId(id: string): LoadedAsset
    {
        const resource = this.loader.resources[id];
        // return the expected resource (in specified detection order
        return (resource as any).sound || resource.spritesheet || resource.texture || resource.data;
    }

    public getAsset<T extends LoadedAsset = LoadedAsset>(id: string): T
    {
        return this.loaded.get(id) as T;
    }

    public getTexture(id:string, atlasId?:string): Texture
    {
        if (atlasId)
        {
            const atlas = this.getSpritesheet(atlasId);
            if (atlas.textures[id])
            {
                return atlas.textures[id];
            }
            throw new Error(`Atlas ${atlasId} does not have a texture ${id}`);
        }
        const asset = this.loaded.get(id);
        if (!asset || !(asset instanceof Texture))
        {
            throw new Error(`Asset ${id} is not a Texture`);
        }
        return asset;
    }

    public getSpritesheet(id:string): Spritesheet
    {
        const asset = this.loaded.get(id);
        if (!asset || !(asset instanceof Spritesheet))
        {
            throw new Error(`Asset ${id} is not a Spritesheet`);
        }
        return asset;
    }

    public getSound(id:string): pixiSound.Sound
    {
        const asset = this.loaded.get(id);
        if (!asset || !(asset instanceof pixiSound.Sound))
        {
            throw new Error(`Asset ${id} is not a Sound`);
        }
        return asset;
    }

    public unload()
    {
        for (const asset of this.loaded.values())
        {
            if (asset && typeof asset.destroy === 'function')
            {
                // destroy sounds/spritesheets/textures, specifically destroying base textures
                asset.destroy(true);
            }
        }
        this.loaded.clear();
        this.loader.reset();
    }

    public destroy()
    {
        this.unload();
        if (LoadContext._current === this)
        {
            LoadContext._current = null;
        }
        this.loader.destroy();
        this.loader = null;
    }
}